/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';

import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import type {
	EditorAppearance,
	ExtractInjectionAPI,
	ToolbarUIComponentFactory,
} from '@atlaskit/editor-common/types';

import type { LoomPlugin } from '../loomPluginType';
import { executeRecordVideo } from '../pm-plugins/commands';
import { type ButtonComponentProps, type LoomPluginOptions } from '../types';

import ToolbarButtonComponent from './ToolbarButtonComponent';

const CustomisableLoomToolbarButton = (
	disabled: boolean,
	appearance: EditorAppearance,
	api: ExtractInjectionAPI<LoomPlugin> | undefined,
) =>
	React.forwardRef<HTMLElement, ButtonComponentProps>((props, ref) => {
		const { onClickBeforeInit, isDisabled = false, href, ...restProps } = props;
		const { loomState, connectivityState } = useSharedPluginState(api, ['loom', 'connectivity']);
		const isLoomEnabled = !!loomState?.isEnabled;
		const handleOnClick = useCallback(
			(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
				if (isLoomEnabled) {
					executeRecordVideo(api);
				} else {
					onClickBeforeInit && onClickBeforeInit(e);
				}
			},
			[isLoomEnabled, onClickBeforeInit],
		);
		return (
			<ToolbarButtonComponent
				ref={ref}
				hideTooltip={!!(restProps.onMouseEnter || restProps.onMouseLeave)}
				// Ignore href if Loom is enabled so that it doesn't interfere with recording
				href={isLoomEnabled ? undefined : href}
				disabled={disabled || isDisabled || connectivityState?.mode === 'offline'}
				api={api}
				appearance={appearance}
				onClick={(e) => handleOnClick(e)}
				// Ignored via go/ees005
				// eslint-disable-next-line react/jsx-props-no-spreading
				{...restProps}
			/>
		);
	});

const LoomToolbarButtonWrapper = ({
	disabled,
	api,
	appearance,
}: {
	disabled: boolean;
	api: ExtractInjectionAPI<LoomPlugin> | undefined;
	appearance: EditorAppearance;
}) => {
	const handleOnClick = useCallback(() => executeRecordVideo(api), [api]);
	const { loomState, connectivityState } = useSharedPluginState(api, ['loom', 'connectivity']);
	if (!loomState) {
		return null;
	}

	return (
		<ToolbarButtonComponent
			// Disable the icon while the SDK isn't initialised
			disabled={disabled || !loomState?.isEnabled || connectivityState?.mode === 'offline'}
			api={api}
			appearance={appearance}
			onClick={handleOnClick}
		/>
	);
};

export const loomPrimaryToolbarComponent =
	(
		config: LoomPluginOptions,
		api: ExtractInjectionAPI<LoomPlugin> | undefined,
	): ToolbarUIComponentFactory =>
	({ disabled, appearance }) => {
		if (config.shouldShowToolbarButton === false) {
			return null;
		}

		if (config.renderButton) {
			return config.renderButton(CustomisableLoomToolbarButton(disabled, appearance, api));
		}

		if (config.shouldShowToolbarButton) {
			return (
				<LoomToolbarButtonWrapper
					// Disable the icon while the SDK isn't initialised
					disabled={disabled}
					api={api}
					appearance={appearance}
				/>
			);
		}

		return null;
	};
